import type { CancelablePromise } from "./core/CancelablePromise"
import { OpenAPI } from "./core/OpenAPI"
import { request as __request } from "./core/request"
import { compressImageForUpload } from "../utils/compressImage"

import {
  Body_login_login_access_token,
  Message,
  NewPassword,
  Token,
  UpdatePassword,
  UserCreate,
  UserPublic,
  UserRegister,
  UserUpdate,
  UserUpdateMe,
  UsersPublic,
  RoomPublic,
  RoomsPublic,
  PreviewDto,
  UserEmployeePublic,
  DirectChat,
  Company,
  Department,
  DepartmentCreate,
  PatternSchedule, ShiftPattern, Notification,
  VacationSickDay, VacationsResponse, CompanyCreate,
  UserConsentRequest, UserConsentResponse,
  DayGroup
} from "./models"

// --- Telemetry types (frontend-only DTOs for convenience)
export type TelemetryDailyDTO = {
  date: string
  api_calls: number
  active_users: number
  push_sent: number
  pdf_generated: number
}

export type TelemetryMonthlyDTO = {
  month: string
  api_calls: number
  active_users: number
  push_sent: number
  pdf_generated: number
}

export type TelemetryInvoiceDTO = {
  month: string
  api_calls: number
  active_users: number
  push_sent: number
  pdf_generated: number
  amount_cents: number
  status: string
}

export type TDataDomainExists = {
  domain: string
}

export type TDataLoginAccessToken = {
  formData: Body_login_login_access_token
}
export type TDataRecoverPassword = {
  email: string
}
export type TDataResetPassword = {
  requestBody: NewPassword
}
export type TDataRecoverPasswordHtmlContent = {
  email: string
}

export class LoginService {
  /**
   * Login Access Token
   * OAuth2 compatible token login, get an access token for future requests
   * @returns Token Successful Response
   * @throws ApiError
   */
  public static loginAccessToken(
    data: TDataLoginAccessToken,
  ): CancelablePromise<Token> {
    const { formData } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/login/access-token",
      body: formData,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Test Token
   * Test access token
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static testToken(): CancelablePromise<UserPublic> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/login/test-token",
    })
  }

  /**
   * Recover Password
   * Password Recovery
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static recoverPassword(
    data: TDataRecoverPassword,
  ): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/password-recovery",
      body: data,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Reset Password
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static resetPassword(
    data: TDataResetPassword,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/reset-password/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Recover Password Html Content
   * HTML Content for Password Recovery
   * @returns string Successful Response
   * @throws ApiError
   */
  public static recoverPasswordHtmlContent(
    data: TDataRecoverPasswordHtmlContent,
  ): CancelablePromise<string> {
    const { email } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/password-recovery-html-content/{email}",
      path: {
        email,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }
}

export type TDataReadUsers = {
  limit?: number
  skip?: number
  company_id?: string
  department_id?: string
  search?: string
}
export type TDataCreateUser = {
  requestBody: UserCreate
}
export type TDataUpdateUserMe = {
  requestBody: UserUpdateMe
}
export type TDataUpdatePasswordMe = {
  requestBody: UpdatePassword
}
export type TDataRegisterUser = {
  requestBody: UserRegister
}
export type TDataReadUserById = {
  userId: string
}
export type TDataUpdateUser = {
  requestBody: UserUpdate
  userId: string
}
export type TDataDeleteUser = {
  userId: string
}

export type HasUserConsentResponse = {
  hasConsent: boolean;
};

export class UsersService {
  /**
   * Read Users
   * Retrieve users.
   * @returns UsersPublic Successful Response
   * @throws ApiError
   */
  public static readUsers(
    data: TDataReadUsers = {},
  ): CancelablePromise<UsersPublic> {
    const { limit = 100, skip = 0, company_id, department_id, search } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/users/",
      query: {
        skip,
        limit,
        company_id,
        department_id,
        search
      },
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Create User
   * Create new user.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static createUser(
    data: TDataCreateUser,
  ): CancelablePromise<UserPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/users/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Read User Me
   * Get current user.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static readUserMe(): CancelablePromise<UserPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/users/me",
    })
  }

  /**
   * Delete User Me
   * Delete own user.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteUserMe(): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/users/me",
    })
  }

  /**
   * Update User Me
   * Update own user.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static updateUserMe(
    data: TDataUpdateUserMe,
  ): CancelablePromise<UserPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/users/me",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Update Password Me
   * Update own password.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static updatePasswordMe(
    data: TDataUpdatePasswordMe,
  ): CancelablePromise<Message> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/users/me/password",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /** Upload current user's avatar (multipart) to local storage and return URL */
  public static async uploadUserAvatar(file: File): Promise<{ avatar: string }> {
    const uploadFile = await compressImageForUpload(file)
    const form = new FormData();
    form.append("avatar", uploadFile);
    return await __request(OpenAPI, {
      method: "POST",
      url: "/v1/users/me/avatar",
      body: form as any,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  /**
   * Register User
   * Create new user without the need to be logged in.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static registerUser(
    data: TDataRegisterUser,
  ): CancelablePromise<UserPublic> {
    const { requestBody } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/users/signup",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Read User By Id
   * Get a specific user by id.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static readUserById(
    data: TDataReadUserById,
  ): CancelablePromise<UserPublic> {
    const { userId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/users/{user_id}",
      path: {
        user_id: userId,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Update User
   * Update a user.
   * @returns UserPublic Successful Response
   * @throws ApiError
   */
  public static updateUser(
    data: TDataUpdateUser,
  ): CancelablePromise<UserPublic> {
    const { requestBody, userId } = data
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/users/{user_id}",
      path: {
        user_id: userId,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Delete User
   * Delete a user.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static deleteUser(data: TDataDeleteUser): CancelablePromise<Message> {
    const { userId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/users/{user_id}",
      path: {
        user_id: userId,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Get user consent status
   * @returns UserConsentResponse Successful Response
   */
  public static getUserConsent(): CancelablePromise<UserConsentResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/users/me/consent",
    });
  }

  /**
   * Create or update user consent
   * @returns Message Successful Response
   */
  public static createOrUpdateUserConsent(data: {
    requestBody: UserConsentRequest
  }): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/users/me/consent",
      body: data.requestBody,
      mediaType: "application/json",
    });
  }

  /**
   * Check if user has given consent
   * @returns boolean Successful Response
   */
  public static hasUserConsent(): CancelablePromise<HasUserConsentResponse> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/users/me/has-consent",
    });
  }


}
export type TDataReadEmployeeById = {
  userId: string
}

export class EmployeeService {
  public static readEmployeeById(
      data: TDataReadEmployeeById,
  ): CancelablePromise<UserEmployeePublic> {
    const { userId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/employees/{user_id}",
      path: {
        user_id: userId,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }
public static updateEmployeeById(
    data: {  id: string; requestBody: Record<string, any> }
): CancelablePromise<any> {
  return __request(OpenAPI, {
    method: "PATCH",
    url: "/v1/employees/{id}",
    path: {
      id: data.id,
    },
    body: data.requestBody,
    mediaType: "application/json",
    errors: {
      400: "Bad Request",
      403: "Forbidden",
      404: "Not Found",
      422: "Validation Error",
    },
  })
}
}

export type TDataTestEmail = {
  emailTo: string
}


export class EmployeesScheduleService {
  public static getAllEmployeesSchedule(): CancelablePromise<{ emp_schedule: DayGroup[] }> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/employees-schedule/",
    })
  }
}

export class UtilsService {
  /**
   * Test Email
   * Test emails.
   * @returns Message Successful Response
   * @throws ApiError
   */
  public static testEmail(data: TDataTestEmail): CancelablePromise<Message> {
    const { emailTo } = data
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/utils/test-email/",
      query: {
        email_to: emailTo,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }

  /**
   * Health Check
   * @returns boolean Successful Response
   * @throws ApiError
   */
  public static healthCheck(): CancelablePromise<boolean> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/utils/health-check/",
    })
  }
}


export class MediaService {
  public static downloadImages(postId: string, data: FormData): Promise<{ url: string }[]> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/media/${postId}/images`,
      body: data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  }

  public static downloadOneImage(data: FormData): CancelablePromise<string> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/media/images`,
      body: data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  }


  static deleteImage(postId: string, imageUrl: string): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/media/images/${postId}`,
      body: JSON.stringify({ imageUrl }), // Передаємо URL як JSON
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  static deleteImageInUrl(imageUrl: string): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/media/images/url`,
      body: JSON.stringify({ imageUrl }), // Передаємо URL як JSON
      headers: {
        "Content-Type": "application/json",
      },
    });
  }
}


export class DocumentService {


    public static userScheduleGetPDF(
        user_id: string,
        startDate: string,
        endDate: string,
        lang?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "GET",
            url: `/v1/document/pdf/report`,
            query: {
                user_id: user_id,
                start: startDate,
                end: endDate,
                lang: lang,
            },
            headers: { Accept: "application/pdf" },
            responseType: "blob",
        })
    }

    public static companyHoursGetPDF(
        company_id: string,
        startDate: string,
        endDate: string,
        lang?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "GET",
            url: `/v1/document/pdf/report-company`,
            query: {
                company_id: company_id,
                start: startDate,
                end: endDate,
                lang: lang,
            },
            headers: { Accept: "application/pdf" },
            responseType: "blob",
        })
    }

    public static departmentHoursGetPDF(
        department_id: string,
        startDate: string,
        endDate: string,
        lang?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "GET",
            url: `/v1/document/pdf/report-department`,
            query: {
                department_id: department_id,
                start: startDate,
                end: endDate,
                lang: lang,
            },
            headers: { Accept: "application/pdf" },
            responseType: "blob",
        })
    }

    public static userScheduleGetXLSX(
      user_id: string,
      startDate: string,
      endDate: string,
      format?: string,
      lang?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "GET",
            url: `/v1/document/report/export/xlsx`,
            query: {
                user_id: user_id,
                start: startDate,
                end: endDate,
                format: format,
                lang: lang,
            },
            // Accept both CSV and XLSX
            headers: { Accept: "*/*" },
            responseType: "blob",
        })
    }

    public static userScheduleGetXML(
        user_id: string,
        startDate: string,
        endDate: string,
        lang?: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "GET",
            url: `/v1/document/report/export/xml`,
            query: {
                user_id: user_id,
                start: startDate,
                end: endDate,
                lang: lang,
            },
            // Accept both CSV and XLSX
            headers: { Accept: "application/xml; charset=utf-8" },
            responseType: "blob",
        })
    }

}


export type TDataReadRooms = {
  limit?: number
  skip?: number
}

export class RoomService {

  /**
   * Create Room
   * Create new Room.
   * @returns RoomPublic Successful Response
   * @throws ApiError
   */
  public static createRoom(data: JSON): CancelablePromise<RoomPublic> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/rooms/",
      body: data,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  public static readRooms(
      data: TDataReadRooms = {},
  ): CancelablePromise<RoomsPublic> {
    const { limit = 100, skip = 0 } = data
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/rooms/",
      query: {
        skip,
        limit,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static readRoomById(
      id: string
  ): CancelablePromise<RoomPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/rooms/${id}`,
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static updateRoom(
      id: string,
      data: JSON,
  ): CancelablePromise<RoomPublic> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/rooms/${id}`,
      body: data,
      errors: {
        422: "Validation Error",
      },
    })
  }

  public static deleteRoom(
      id: string
  ): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/rooms/${id}`,
      errors: {
        422: "Validation Error",
      },
    })
  }
}

// Company news service
import type { CompanyNews, CompanyNewsCreate, CompanyNewsUpdate } from "./models"
export class CompanyNewsService {
  public static list(): CancelablePromise<CompanyNews[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/company-news/",
    })
  }
  public static getById(id: string): CancelablePromise<CompanyNews> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/company-news/${id}`,
    })
  }
  public static create(data: CompanyNewsCreate): CancelablePromise<CompanyNews> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/company-news/",
      body: data,
      headers: { "Content-Type": "application/json" },
    })
  }
  public static update(id: string, data: CompanyNewsUpdate): CancelablePromise<CompanyNews> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: `/v1/company-news/${id}`,
      body: data,
      headers: { "Content-Type": "application/json" },
    })
  }
  public static remove(id: string): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/company-news/${id}`,
    })
  }
}

export type CreateDirectChatPayload = {
  user_id: string;
};
export class DirectService {
    public static readUsers(): CancelablePromise<UserPublic[]> {
        return __request(OpenAPI, {
        method: "GET",
        url: "/v1/direct/users",
        })
    }

  public static getOrPostChats(
      data: CreateDirectChatPayload
  ): CancelablePromise<DirectChat> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/direct/chats",
      body: data,
      headers: {
        "Content-Type": "application/json",
      },
    })
  }

    public static readMessages(userId: string): CancelablePromise<Message[]> {
        return __request(OpenAPI, {
        method: "GET",
        url: `/v1/direct/${userId}/messages`,
        })
    }

    public static createMessage(data: JSON): CancelablePromise<Message> {
        return __request(OpenAPI, {
        method: "POST",
        url: "/v1/direct/messages",
        body: data,
        headers: {
            "Content-Type": "application/json",
        },
        })
    }
}

export type TDataScheduleConfirm = {
  ID: string
  user_id: string
  start_at: string
  end_at: string
  note?: string
}

export type TDataScheduleAutoSchedule = {
  ID: string
  user_id: string
  note?: string
}

export type TDataScheduleStartStop = {
  ID: string
}

export type TDataApplyTemplateToUser = {
  user_id: string[]
  pattern_id: string
  start_date: string
  months: number
}

export class ScheduleService {
  public static getScheduleByUserId(
      user_id: string,
      startDate: string,
      endDate: string
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/work-schedule/get`,
      query: {
        user_id: user_id,
        start: startDate,
        end: endDate,
      },
    })
  }

  public static getScheduleById(
        schedule_id: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "GET",
            url: `/v1/work-schedule/get-id`,
            query: {
                schedule_id: schedule_id,
            },
        })
    }

  public static confirmScheduleAdminTime(
        data: TDataScheduleConfirm
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
        method: "PATCH",
        url: `/v1/work-schedule/confirm`,
        body: data,
        headers: {
            "Content-Type": "application/json",
        },
        })
    }

  public static startScheduleUser(
        data: TDataScheduleStartStop
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
        method: "POST",
        url: `/v1/work-schedule/start`,
        body: data,
        headers: {
            "Content-Type": "application/json",
        },
        })
    }

  public static stopScheduleUser(
      data: TDataScheduleStartStop
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/work-schedule/stop`,
      body: data,
      headers: {
        "Content-Type": "application/json",
      },
    })
  }

  public static autoScheduleUserConfirm(
      data: TDataScheduleAutoSchedule
    ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/work-schedule/auto-confirm`,
      body: data,
      headers: {
        "Content-Type": "application/json",
      },
    })
  }

  public static applyTemplateToUser(
      data: TDataApplyTemplateToUser
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: `/v1/work-schedule/patterns/apply`,
      body: data,
      headers: {
        "Content-Type": "application/json",
      },
    })
  }
}

type TDataPatternSchedule = {
  name: string;
  description?: string;
  cycle_length: number;
  pattern: ShiftPattern[];
};

type TDataPatternID = {
  patternId: string
}
export class PatternScheduleService {
  public static getPatternsSchedule(
  ): CancelablePromise<PatternSchedule> {
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/work-schedule/patterns/get`,
    })
  }
    public static createPatternSchedule(
        data: TDataPatternSchedule
    ): CancelablePromise<PatternSchedule> {
        return __request(OpenAPI, {
        method: "POST",
        url: `/v1/work-schedule/patterns/create`,
        body: data,
        headers: {
            "Content-Type": "application/json",
        },
        })
    }
  public static getPatternScheduleById(
      data: TDataPatternID
  ): CancelablePromise<Message> {
    const { patternId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/work-schedule/patterns/get/{pattern_Id}`,
      path: { pattern_Id: patternId },
    })
  }

    public static deletePatternSchedule(
        data: TDataPatternID
    ): CancelablePromise<Message> {
      const { patternId } = data
        return __request(OpenAPI, {
        method: "DELETE",
        url: `/v1/work-schedule/patterns/delete/{pattern_Id}`,
        path: { pattern_Id: patternId },
        })
    }
}

export type TDataVacationSick = {
  start_date: string
  end_date: string
  type: string
  hours?: number
}

export type TDataVacationSickUpdate = {
  ID: string
  user_id: string
  status: string
}

export type TDataVacationSickCancel = {
    id: string
    user_id: string
}

export class VacationSickService {

    public static getAllVacationsSick(
        params?: { skip?: number; limit?: number }
      ) : CancelablePromise<VacationSickDay[]> {
        return __request(OpenAPI, {
          method: "GET",
          url: `/v1/vacation-sick/get`,
          query: {
            skip: params?.skip,
            limit: params?.limit,
          },
        })
      }

    public static getVacationsUser(
        ) : CancelablePromise<VacationsResponse> {
        return __request(OpenAPI, {
            method: "GET",
            url: `/v1/vacation-sick/get-by-user`,
        })
    }

    public static createVacationSick(data: TDataVacationSick): CancelablePromise<any> {
        return __request(OpenAPI, {
        method: "POST",
        url: `/v1/vacation-sick/create`,
        body: data,
        headers: {
            "Content-Type": "application/json",
        },
        })
    }

    public static updateVacationSick(data: TDataVacationSickUpdate): CancelablePromise<any> {
      return __request(OpenAPI, {
        method: "PATCH",
        url: `/v1/vacation-sick/update`,
        body: data,
        headers: {
            "Content-Type": "application/json",
        },
      })
    }

    public static cancelApprovedVacationSick(data: TDataVacationSickCancel): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "PATCH",
            url: `/v1/vacation-sick/canceled`,
            body: data,
            headers: {
                "Content-Type": "application/json",
            },
        })
    }
}

export class PreviewService {

  public static getPreview(url: string): CancelablePromise<PreviewDto> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/link-preview",
      query: {
        url, // ?url=https://...
      },
    });
  }
}

type TDataCompanyId = {
  companyId: string
}

export type TDataCompanyCreate = {
  name?: string
  address?: string
  description?: string
  phone?: string
  email?: string
  website?: string
  extra_data?: string
}

export class CompanyService {

  public static createCompany(
      requestBody: CompanyCreate
  ): CancelablePromise<Company> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/companies/",
      body: requestBody,
      mediaType: "application/json",
    });
  }

  public static getCompanies(): CancelablePromise<Company[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/companies/",
    });
  }

  public static getCompanyById(
      data: TDataCompanyId
  ): CancelablePromise<Company> {
    const { companyId } = data
    return __request(OpenAPI, {
      method: "GET",
      url: `/v1/companies/{company_id}`,
      path: {
        company_id: companyId,
      },
    });
  }

  public static updateCompanyById(data: {
    id: string;
    requestBody: TDataCompanyCreate;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "PATCH",
      url: "/v1/companies/{id}",
      path: {
        id: data.id,
      },
      body: data.requestBody,
      mediaType: "application/json",
    });
  }

  public static deleteCompanyById(data: TDataCompanyId): CancelablePromise<Message> {
    const { companyId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/companies/{comp_id}",
      path: {
        comp_id: companyId,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }


}

type TDataDepartmentID = {
    departmentId: string
    }

type TDataDepartmentUpdate = {
    name: string;
    description?: string;
    extra_data?: string;
};

export class DepartmentService {
  public static createDepartment(
      requestBody: DepartmentCreate,
  ): CancelablePromise<Department> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/departments/",
      body: requestBody,
      mediaType: "application/json",
      errors: {
        422: "Validation Error",
      },
    })
  }


    public static getDepartments(): CancelablePromise<Department[]> {
        return __request(OpenAPI, {
        method: "GET",
        url: "/v1/departments/",
        });
    }



    public static getDepartmentById(data: TDataDepartmentID): CancelablePromise<Department> {
      const { departmentId } = data
        return __request(OpenAPI, {
        method: "GET",
        url: `/v1/departments/{dept_id}`,
        path: {
          dept_id: departmentId,
        },
        });
    }

    public static updateDepartmentById(data: {
        id: string;
        requestBody: TDataDepartmentUpdate;
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: "PATCH",
            url: "/v1/departments/{dept_id}",
            path: {
                dept_id: data.id,
            },
            body: data.requestBody,
            mediaType: "application/json",
        });
    }

  public static deleteDepartmentById(data: TDataDepartmentID): CancelablePromise<Message> {
    const { departmentId } = data
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/departments/{dept_id}",
      path: {
        dept_id: departmentId,
      },
      errors: {
        422: "Validation Error",
      },
    })
  }
}

type TDataUnreadNotifications = {
  unread: boolean
}

export class NotificationService {
  public static getNotifications(
      data: TDataUnreadNotifications
  ): CancelablePromise<Notification[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/notifications/get",
      body: data,
    });
  }

  public static markNotificationAsRead(
      id: string // UUID події
  ): CancelablePromise<Notification> {
    return __request(OpenAPI, {
      method: "PATCH", // або PUT, залежно від бекенду
      url: `/v1/notifications/mark/${id}`,
    });
  }

  public static deleteNotification(
      id: string // UUID події
  ): CancelablePromise<Message> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/notifications/delete/${id}`,
    });
  }

}

type TDataTokenRegister = {
  token: string
}

export class FCMNotification{
  public static sendTokenRegister(
      data: TDataTokenRegister
  ): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/push/register",
      body: data,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }
}

// QR Services
export type QrIssueResp = { token: string; exp: number; kid: string }
export type QrSimpleResp = { code: string }

// Telemetry Services
export type TelemetryStorageUsageDTO = { file_count: number; used_bytes: number }
export class TelemetryService {
  public static getDaily(params?: { start?: string; end?: string }): CancelablePromise<{ items: TelemetryDailyDTO[] }> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/telemetry/daily",
      query: params || {},
    })
  }
  public static getMonthly(): CancelablePromise<{ items: TelemetryMonthlyDTO[] }> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/telemetry/monthly",
    })
  }
  public static getInvoice(): CancelablePromise<TelemetryInvoiceDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/telemetry/invoice",
    })
  }
  public static aggregateMonthly(): CancelablePromise<Message | any> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/telemetry/aggregate/monthly",
    })
  }
  public static generateInvoice(): CancelablePromise<TelemetryInvoiceDTO | any> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/telemetry/invoice",
    })
  }
  public static getStorageUsage(): CancelablePromise<TelemetryStorageUsageDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/telemetry/storage",
    })
  }
}

// Admin company info
export type AdminTariffDTO = {
  id: string
  slug: string
  name: string
  price_eur_cents?: number | null
  included_mau: number
  included_api_per_month: number
  included_push_per_month: number
  included_pdf_per_month: number
  included_storage_gb: number
  is_custom: boolean
  active: boolean
  sort_order: number
  notes?: string | null
}

export type AdminCompanyInfoDTO = {
  id: string
  name: string
  domain: string
  legal_name?: string | null
  nip?: string | null
  regon?: string | null
  krs?: string | null
  address?: string | null
  phone?: string | null
  email?: string | null
  website?: string | null
  contact_person?: string | null
  bank_name?: string | null
  bank_account?: string | null
  iban?: string | null
  swift?: string | null
  description?: string | null
  active: boolean
  active_accounts: number
  tariff: AdminTariffDTO | null
  logo?: string
  self_hosted?: boolean
}

export class AdminService {
  public static getCompanyInfo(): CancelablePromise<AdminCompanyInfoDTO> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/admin/company",
    })
  }
}

export class QrService {
  public static issue(): CancelablePromise<QrIssueResp> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/qr/issue",
    })
  }
  public static issueSimple(data?: { user_id?: string }): CancelablePromise<QrSimpleResp> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/qr/issue-simple",
      body: data || {},
      headers: { "Content-Type": "application/json" },
    })
  }
  public static verify(data: { token: string }): CancelablePromise<{ valid: boolean; instance: string; userId: string }> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/qr/verify",
      body: data,
      headers: {
        "Content-Type": "application/json",
      },
    })
  }
}

// Shift Entries Service
export type ShiftInfoDTO = {
  id: string;
  name_shift: string;
  description?: string | null;
  status: string; // "open" | "closed"
  company_id?: string | null;
  company_name?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  who_created_by_id?: string;
  who_created_by_acron?: string;
  created_at?: string;
  closed_at?: string | null;
}

export type ShiftEntryDTO = {
  id: string;
  user_id: string;
  fullName: string;
  acronym?: string;
  avatar?: string | null;
  start_at: string;
  end_at?: string | null;
  shift_id: string;
  shift_name: string;
  department_id?: string | null;
  department_name?: string | null;
  company_id?: string | null;
  company_name?: string | null;
}

export class ShiftEntriesService {
  static listShifts(params?: { from?: string; to?: string; status?: string; companyId?: string; departmentId?: string; limit?: number; offset?: number }): CancelablePromise<{ data: ShiftInfoDTO[]; count: number }> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/shift-entries/shifts",
      query: params || {},
    })
  }
  static getShiftEntries(shiftId: string, params?: { limit?: number; offset?: number }): CancelablePromise<{ data: ShiftEntryDTO[]; count: number }> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/shift-entries/shifts/{id}/entries",
      path: { id: shiftId },
      query: params || {},
    })
  }
  static openShift(body: { name_shift?: string; description?: string | null }): CancelablePromise<{ status: string; shift: ShiftInfoDTO }> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/shift-entries/shifts/open",
      body,
      headers: { "Content-Type": "application/json" },
    })
  }
  static closeShift(body?: { shift_id?: string }): CancelablePromise<{ status: string; shift: ShiftInfoDTO }> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/shift-entries/shifts/close",
      body: body || {},
      headers: { "Content-Type": "application/json" },
    })
  }
  static reopenShift(shiftId: string): CancelablePromise<{ status: string; shift: ShiftInfoDTO }> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/shift-entries/shifts/{id}/open",
      path: { id: shiftId },
    })
  }
  static createShiftEntry(shiftId: string, body: { user_id: string; start_at?: string }): CancelablePromise<{ status: string; entry: ShiftEntryDTO }> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/shift-entries/shifts/{id}/entries",
      path: { id: shiftId },
      body,
      headers: { "Content-Type": "application/json" },
    })
  }
}

// ---- Documents storage (public listing + admin upload/delete)
export type PublicDocument = {
  id: string
  name: string
  url: string
  size: number
  created_at: string
  created_by?: string
  original_name?: string
}

export class DocumentsStorageService {
  public static listPublic(): CancelablePromise<PublicDocument[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/public/documents",
    })
  }

  public static upload(formData: FormData): CancelablePromise<PublicDocument[]> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/documents/upload",
      body: formData,
      headers: { "Content-Type": "multipart/form-data" },
    })
  }

  public static delete(id: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/documents/${id}`,
    })
  }

  public static download(id: string): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/documents/{id}/download",
      path: { id },
      responseType: "blob",
    })
  }
}


// ---- Employee documents (superadmin-only, per user)
export type EmployeeDocument = {
  id: string
  user_id: string
  name: string
  url: string
  size: number
  created_at: string
  created_by?: string
  original_name?: string
}

export class EmployeeDocumentsService {
  public static list(userId: string): CancelablePromise<EmployeeDocument[]> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/employee-documents",
      query: { user_id: userId },
    })
  }

  public static upload(formData: FormData): CancelablePromise<EmployeeDocument[]> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/employee-documents/upload",
      body: formData,
      headers: { "Content-Type": "multipart/form-data" },
    })
  }

  public static delete(id: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: `/v1/employee-documents/${id}`,
    })
  }

  public static download(id: string): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/employee-documents/{id}/download",
      path: { id },
      responseType: "blob",
    })
  }
}
